import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { SearchIcon, ChevronRightIcon, LibraryIcon, ArrowRightIcon, FilterIcon, StarIcon, BookOpenIcon, ClockIcon, CheckCircleIcon, SparklesIcon } from 'lucide-react';
import { FilterSidebar, FilterSidebarWrapper, FilterSection } from '../components/UI/FilterSidebar';
import { SearchWithAutocomplete } from '../components/UI/SearchWithAutocomplete';
import { useDarkClasses } from '../hooks/useDarkClasses';
import { useRoleTheme } from '../hooks/useRoleTheme';
import SeriesCarousel from '../components/Books/SeriesCarousel';
import { SeriesStatusBadge } from '../components/Books/SeriesStatusBadge';
// Extended mock data for series with new status fields
const seriesData = [{
  id: 1,
  name: 'Echo Series',
  author: 'Elena Richards',
  books: [{
    id: 101,
    title: 'The Silent Echo',
    coverImage: 'https://images.unsplash.com/photo-1544947950-fa07a98d237f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    releaseDate: '2018-06-12',
    position: 1
  }, {
    id: 102,
    title: 'Echoes of Darkness',
    coverImage: 'https://images.unsplash.com/photo-1621351183012-e2f9972dd9bf?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    releaseDate: '2020-03-24',
    position: 2
  }, {
    id: 103,
    title: 'The Final Echo',
    coverImage: 'https://images.unsplash.com/photo-1543002588-bfa74002ed7e?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    releaseDate: '2023-08-16',
    position: 3
  }],
  totalBooks: 3,
  genres: ['Mystery', 'Thriller'],
  rating: 4.8,
  reviews: 1245,
  coverImage: 'https://images.unsplash.com/photo-1544947950-fa07a98d237f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  description: 'A gripping series following investigative journalist Mira Hayes as she uncovers a conspiracy that could change everything.',
  status: 'complete',
  completedDate: '2023-08-16',
  popularity: 95,
  connectionType: 'connected',
  endingType: 'HEA',
  contentWarnings: ['Violence', 'Dark Themes']
}, {
  id: 2,
  name: 'Mystical Realms',
  author: 'Marcus Chen',
  books: [{
    id: 201,
    title: 'Beyond the Veil',
    coverImage: 'https://images.unsplash.com/photo-1543002588-bfa74002ed7e?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    releaseDate: '2019-04-18',
    position: 1
  }, {
    id: 202,
    title: 'Realm of Shadows',
    coverImage: 'https://images.unsplash.com/photo-1589998059171-988d887df646?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    releaseDate: '2020-11-05',
    position: 2
  }, {
    id: 203,
    title: 'The Crystal Kingdom',
    coverImage: 'https://images.unsplash.com/photo-1518744386442-2d48ac55263f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    releaseDate: '2021-07-22',
    position: 3
  }, {
    id: 204,
    title: 'The Final Enchantment',
    coverImage: 'https://images.unsplash.com/photo-1603162525937-92a06da4b8ba?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    releaseDate: '2023-01-10',
    position: 4
  }],
  totalBooks: 4,
  genres: ['Fantasy', 'Romance'],
  rating: 4.6,
  reviews: 892,
  coverImage: 'https://images.unsplash.com/photo-1543002588-bfa74002ed7e?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  description: 'Journey through magical worlds and epic love stories in this enchanting fantasy series.',
  status: 'backlist',
  completedDate: '2023-01-10',
  popularity: 88,
  spinOffName: 'Crystal Chronicles',
  spinOffId: '99',
  connectionType: 'interconnected',
  endingType: 'HFN'
}, {
  id: 3,
  name: 'Midnight Chronicles',
  author: 'Sophia Williams',
  books: [{
    id: 301,
    title: 'Midnight Shadows',
    coverImage: 'https://images.unsplash.com/photo-1621351183012-e2f9972dd9bf?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    releaseDate: '2021-09-30',
    position: 1
  }, {
    id: 302,
    title: 'Midnight Whispers',
    coverImage: 'https://images.unsplash.com/photo-1633477189729-9290b3261d0a?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    releaseDate: '2022-11-15',
    position: 2
  }, {
    id: 303,
    title: 'Midnight Secrets',
    coverImage: 'https://images.unsplash.com/photo-1629992101753-56d196c8aabb?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    releaseDate: '2024-04-22',
    position: 3,
    status: 'upcoming'
  }],
  totalBooks: 5,
  genres: ['Horror', 'Supernatural'],
  rating: 4.7,
  reviews: 756,
  coverImage: 'https://images.unsplash.com/photo-1621351183012-e2f9972dd9bf?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  description: 'A terrifying journey into the supernatural that will keep you awake at night.',
  status: 'ongoing',
  popularity: 82,
  connectionType: 'connected',
  preorderBook: {
    number: 4,
    id: '304'
  }
}, {
  id: 4,
  name: 'The Explorer Chronicles',
  author: 'David Miller',
  books: [{
    id: 401,
    title: 'The Lost City',
    coverImage: 'https://images.unsplash.com/photo-1531928351158-2f736078e0a1?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    releaseDate: '2020-02-28',
    position: 1
  }, {
    id: 402,
    title: 'The Ancient Temple',
    coverImage: 'https://images.unsplash.com/photo-1580934738404-5eb8d4c0389e?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    releaseDate: '2021-05-17',
    position: 2
  }],
  totalBooks: 4,
  genres: ['Adventure', 'Historical Fiction'],
  rating: 4.5,
  reviews: 623,
  coverImage: 'https://images.unsplash.com/photo-1531928351158-2f736078e0a1?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  description: 'An epic adventure through uncharted territories and ancient civilizations.',
  status: 'hiatus',
  popularity: 78,
  connectionType: 'connected'
}, {
  id: 5,
  name: 'Quantum Trilogy',
  author: 'Alex Zhang',
  books: [{
    id: 501,
    title: 'Quantum Dreams',
    coverImage: 'https://images.unsplash.com/photo-1518744386442-2d48ac55263f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    releaseDate: '2022-03-08',
    position: 1
  }, {
    id: 502,
    title: 'Quantum Reality',
    coverImage: 'https://images.unsplash.com/photo-1614332287897-cdc485fa562d?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    releaseDate: '2023-06-20',
    position: 2
  }],
  totalBooks: 3,
  genres: ['Science Fiction', 'Thriller'],
  rating: 4.4,
  reviews: 489,
  coverImage: 'https://images.unsplash.com/photo-1518744386442-2d48ac55263f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  description: 'A mind-bending journey through alternate realities and quantum physics.',
  status: 'ongoing',
  popularity: 75,
  connectionType: 'interconnected'
}];
const Series = () => {
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  const [searchTerm, setSearchTerm] = useState('');
  // Sidebar state
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [selectedGenres, setSelectedGenres] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState('popularity');
  const activeFilterCount = selectedStatuses.length + selectedGenres.length + (sortBy !== 'popularity' ? 1 : 0);
  const filterSections: FilterSection[] = [{
    id: 'sort',
    title: 'Sort By',
    type: 'radio',
    options: [{
      value: 'popularity',
      label: 'Most Popular'
    }, {
      value: 'rating',
      label: 'Highest Rated'
    }, {
      value: 'newest',
      label: 'Newest First'
    }, {
      value: 'alpha',
      label: 'A–Z'
    }],
    value: sortBy,
    onChange: setSortBy
  }, {
    id: 'status',
    title: 'Series Status',
    type: 'checkbox',
    options: [{
      value: 'complete',
      label: 'Complete'
    }, {
      value: 'backlist',
      label: 'Backlist'
    }, {
      value: 'ongoing',
      label: 'Ongoing'
    }, {
      value: 'hiatus',
      label: 'Hiatus'
    }],
    value: selectedStatuses,
    onChange: (val: string) => setSelectedStatuses(prev => prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val])
  }, {
    id: 'genre',
    title: 'Genre',
    type: 'checkbox',
    options: [{
      value: 'Fantasy',
      label: 'Fantasy'
    }, {
      value: 'Romance',
      label: 'Romance'
    }, {
      value: 'Mystery',
      label: 'Mystery'
    }, {
      value: 'Thriller',
      label: 'Thriller'
    }, {
      value: 'Science Fiction',
      label: 'Sci-Fi'
    }, {
      value: 'Horror',
      label: 'Horror'
    }, {
      value: 'Historical Fiction',
      label: 'Historical'
    }, {
      value: 'Adventure',
      label: 'Adventure'
    }, {
      value: 'Contemporary',
      label: 'Contemporary'
    }],
    value: selectedGenres,
    onChange: (val: string) => setSelectedGenres(prev => prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val])
  }];
  const resetFilters = () => {
    setSelectedStatuses([]);
    setSelectedGenres([]);
    setSortBy('popularity');
    setSearchTerm('');
  };
  // Apply filters to seriesData
  const applyFilters = (data: typeof seriesData) => {
    let result = [...data];
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      result = result.filter(s => s.name.toLowerCase().includes(q) || s.author.toLowerCase().includes(q));
    }
    if (selectedStatuses.length > 0) {
      result = result.filter(s => selectedStatuses.includes(s.status));
    }
    if (selectedGenres.length > 0) {
      result = result.filter(s => s.genres.some(g => selectedGenres.includes(g)));
    }
    if (sortBy === 'rating') result.sort((a, b) => b.rating - a.rating);else if (sortBy === 'alpha') result.sort((a, b) => a.name.localeCompare(b.name));else result.sort((a, b) => b.popularity - a.popularity);
    return result;
  };
  // Categorize series for discovery sections
  const completedSeries = applyFilters(seriesData.filter(s => s.status === 'complete' || s.status === 'backlist'));
  const ongoingSeries = applyFilters(seriesData.filter(s => s.status === 'ongoing'));
  const fantasySeries = applyFilters(seriesData.filter(s => s.genres.includes('Fantasy')));
  const thrillerSeries = applyFilters(seriesData.filter(s => s.genres.includes('Thriller')));
  const featuredSeries = seriesData.find(s => s.id === 1);
  // Helper: match UnifiedBookCard genre color logic
  const getGenreColor = (genre: string): string => {
    const g = genre.toLowerCase();
    if (g.includes('romance')) return 'bg-pink-600';
    if (g.includes('fantasy')) return 'bg-indigo-600';
    if (g.includes('sci-fi') || g.includes('science')) return 'bg-blue-600';
    if (g.includes('mystery')) return 'bg-indigo-600';
    if (g.includes('thriller')) return 'bg-red-600';
    if (g.includes('horror')) return 'bg-gray-800';
    if (g.includes('historical')) return 'bg-amber-600';
    if (g.includes('young adult') || g.includes('ya')) return 'bg-cyan-600';
    if (g.includes('contemporary')) return 'bg-emerald-600';
    return 'bg-violet-600';
  };
  return <div className={`min-h-screen ${dk.page}`} data-id="element-12717" data-mp="285bdu08qqutd">
      {/* Header */}
      <div className={`py-6 px-4 ${dk.surface}`} data-id="element-12718" data-mp="2m15drtrje9uc">
        <div className="max-w-7xl mx-auto" data-id="element-12719" data-mp="2h3wvzuaaw7tm">
          <div className={`flex items-center text-sm mb-4 ${dk.textMuted}`} data-id="element-12720" data-mp="2h33tx6mae6ht">
            <Link to="/" className={`hover:${dk.textPrimary} transition-colors`} data-id="element-12721" data-mp="2vrhwrhjpduyd">
              Home
            </Link>
            <ChevronRightIcon className="h-4 w-4 mx-2" data-id="element-12722" data-mp="34t7o6ndl4tw5" />
            <Link to="/browse" className={`hover:${dk.textPrimary} transition-colors`} data-id="element-12723" data-mp="34tr3dwbup46h">
              Books
            </Link>
            <ChevronRightIcon className="h-4 w-4 mx-2" data-id="element-12724" data-mp="3jfn35h5k9s48" />
            <span className={dk.textPrimary} data-id="element-12725" data-mp="3je3mvqjwnh2g">Series Discovery</span>
          </div>

          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6" data-id="element-12726" data-mp="3oe83dgktcbso">
            <div className="flex items-center gap-3" data-id="element-12727" data-mp="3aeypwonzrvse">
              <LibraryIcon className={`h-8 w-8 ${rt.textPrimary}`} data-id="element-12728" data-mp="3egaf4t2yef02" />
              <div data-id="element-12729" data-mp="3eio90ylwdl61">
                <h1 className={`text-2xl md:text-3xl font-bold ${dk.textHeading}`} data-id="element-12730" data-mp="30ox11unte9cf">
                  Series Discovery
                </h1>
                <p className={`mt-1 text-sm ${dk.textSecondary}`} data-id="element-12731" data-mp="35hylsjapd33x">
                  Find your next obsession — from binge-ready complete sets to
                  active ongoing sagas.
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3 w-full md:w-auto" data-id="element-12732" data-mp="2hrm2kca0bjy1">
              <div className="flex-grow md:w-80" data-id="element-12733" data-mp="2mpe3twwjkiew">
                <SearchWithAutocomplete value={searchTerm} onChange={setSearchTerm} placeholder="Search series, authors, or tropes..." variant="rounded" suggestions={[]} onSearch={query => setSearchTerm(query)} data-id="element-12734" data-mp="2mp4g6fyyyncd" />
              </div>
              <Link to="/series/all" className={`flex items-center px-4 py-2.5 rounded-lg font-medium transition-colors whitespace-nowrap ${dk.card} ${dk.textPrimary} border ${dk.border}`} data-id="element-12735" data-mp="20y33i6e5luot">
                Browse All
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content with Sidebar */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-12736" data-mp="1hyp61eh7u1tk">
        {/* Mobile Filter Button */}
        <div className="mb-6 lg:hidden" data-id="element-12737" data-mp="3ge1sjzc2wzz7">
          <button onClick={() => setMobileFiltersOpen(true)} className={`flex items-center gap-2 px-4 py-2 rounded-lg border shadow-sm ${dk.card} ${dk.textSecondary}`} data-id="element-12738" data-mp="3gd0ern3ohzds">
            <FilterIcon className="h-4 w-4" data-id="element-12739" data-mp="y4fnyaphhhy6" />
            Filters
            {activeFilterCount > 0 && <span className={`ml-1 px-1.5 py-0.5 ${rt.badgeRadius} text-xs font-bold text-white ${rt.bgDark}`} data-id="element-12740" data-mp="1327hf95s1c4h">
                {activeFilterCount}
              </span>}
          </button>
        </div>

        <div className="flex gap-8" data-id="element-12741" data-mp="f6mm7bqlje2d">
          {/* Sidebar */}
          <FilterSidebarWrapper isOpen={mobileFiltersOpen} onClose={() => setMobileFiltersOpen(false)} isCollapsed={sidebarCollapsed} onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)} data-id="element-12742" data-mp="f8657a3n037u">
            <FilterSidebar sections={filterSections} onReset={resetFilters} data-id="element-12743" data-mp="t6xbiac9cklf" />
          </FilterSidebarWrapper>

          {/* Discovery Sections */}
          <div className="flex-1 min-w-0 space-y-12" data-id="element-12744" data-mp="1zoapet71jd52">
            {/* Hero: Just Completed Spotlight */}
            {featuredSeries && <section className={`rounded-xl overflow-hidden border ${dk.border} ${dk.card}`} data-id="element-12745" data-mp="4i0atd3upsw1">
                <div className="flex flex-col md:flex-row md:max-h-48" data-id="element-12746" data-mp="gn1uu5gp9kw">
                  <div className="w-full md:w-1/3 relative" data-id="element-12747" data-mp="efwke0h54fs1">
                    <img src={featuredSeries.coverImage} alt={featuredSeries.name} className="w-full h-48 md:h-full object-cover" data-id="element-12748" data-mp="9hv1pgnmmih3" />
                    {featuredSeries.genres[0] && <span className={`absolute top-3 left-3 text-white text-xs font-medium px-2 py-0.5 ${rt.badgeRadius} ${getGenreColor(featuredSeries.genres[0])}`} data-id="element-12749" data-mp="3d0ej1o4ro3x4">
                        {featuredSeries.genres[0]}
                      </span>}
                    <div className="absolute top-3 right-3" data-id="element-12750" data-mp="2z4u0wuwv3pqr">
                      <SeriesStatusBadge status={featuredSeries.status} data-id="element-12751" data-mp="2u7ap01nmzp1z" />
                    </div>
                    <div className="absolute bottom-2 left-2 md:hidden flex items-center gap-1 bg-black/60 rounded-full px-2 py-0.5" data-id="element-12752" data-mp="19p9n9894exud">
                      <StarIcon className="h-3 w-3 text-amber-400 fill-amber-400" data-id="element-12753" data-mp="1eim9jbqbusjy" />
                      <span className="text-white text-xs font-semibold" data-id="element-12754" data-mp="10r7o6y8y6f9q">
                        {featuredSeries.rating}
                      </span>
                      <span className="text-white/70 text-xs" data-id="element-12755" data-mp="1sg0zqioen4cz">
                        ({featuredSeries.reviews.toLocaleString()})
                      </span>
                    </div>
                  </div>
                  <div className={`w-full md:w-2/3 p-4 flex flex-col justify-between ${dk.card}`} data-id="element-12756" data-mp="1jgnu613eizxj">
                    <div data-id="element-12757" data-mp="1od4hit2s1a9k">
                      <div className="flex items-center justify-between gap-2 mb-1" data-id="element-12758" data-mp="80pcx9y2l7l8">
                        <div className="flex items-center gap-2" data-id="element-12759" data-mp="83248dppwuay">
                          <SparklesIcon className={`h-4 w-4 ${rt.textPrimary}`} data-id="element-12760" data-mp="d1ddam86v0s4" />
                          <span className={`text-xs font-semibold uppercase tracking-wide ${rt.textPrimary}`} data-id="element-12761" data-mp="13nj4hu85o8a0">
                            Just Completed
                          </span>
                        </div>
                        <span className={`text-xs ${dk.textMuted}`} data-id="element-12762" data-mp="prxg9p9qxsin">
                          {featuredSeries.totalBooks} books
                        </span>
                      </div>
                      <h2 className={`text-lg font-bold mb-0.5 ${dk.textHeading}`} data-id="element-12763" data-mp="1ig34wn3x81nr">
                        {featuredSeries.name}
                      </h2>
                      <p className={`text-xs mb-2 ${dk.textSecondary}`} data-id="element-12764" data-mp="18ofg6tyr0ypz">
                        by {featuredSeries.author}
                      </p>
                      <p className={`text-xs mb-2 line-clamp-2 ${dk.textSecondary}`} data-id="element-12765" data-mp="21dw3xebfbrfv">
                        {featuredSeries.description}
                      </p>
                    </div>
                    <div className="flex items-center justify-between gap-3" data-id="element-12766" data-mp="2don3d0kf06b4">
                      <div className="flex items-center gap-1" data-id="element-12767" data-mp="2imoy4ir7du2f">
                        <StarIcon className="h-4 w-4 text-amber-400 fill-amber-400" data-id="element-12768" data-mp="2ipb2k9isrtgf" />
                        <span className={`text-sm font-semibold ${dk.textPrimary}`} data-id="element-12769" data-mp="2njyllsv17vnm">
                          {featuredSeries.rating}
                        </span>
                        <span className={`text-xs hidden md:inline ${dk.textMuted}`} data-id="element-12770" data-mp="31dg3l982l0bj">
                          ({featuredSeries.reviews.toLocaleString()} reviews)
                        </span>
                      </div>
                      <Link to={`/series/${featuredSeries.id}`} className={`flex items-center justify-center px-4 py-1.5 rounded-lg font-medium text-sm text-white transition-colors ${rt.primaryButton}`} data-id="element-12771" data-mp="3bdgq1418jwjy">
                        View Series <ArrowRightIcon className="h-4 w-4 ml-1" data-id="element-12772" data-mp="nnjrd6nwnca3" />
                      </Link>
                    </div>
                  </div>
                </div>
              </section>}

            {/* Complete & Binge-Ready */}
            {completedSeries.length > 0 && <section data-id="element-12773" data-mp="11lzw4s27ispl">
                <div className="flex items-center justify-between mb-4" data-id="element-12774" data-mp="11kouaxsbnv14">
                  <div className="flex items-center gap-2" data-id="element-12775" data-mp="16kkxwvx3xkg5">
                    <CheckCircleIcon className="h-6 w-6 text-green-500" data-id="element-12776" data-mp="1bc2pwuh5kaoz" />
                    <div data-id="element-12777" data-mp="1beyq6pgh3bgl">
                      <h2 className={`text-xl font-bold ${dk.textHeading}`} data-id="element-12778" data-mp="1gc76eki295f7">
                        Complete & Binge-Ready
                      </h2>
                      <p className={`text-sm ${dk.textSecondary}`} data-id="element-12779" data-mp="1kf3v8sy71xrl">
                        No cliffhangers. Read the full story today.
                      </p>
                    </div>
                  </div>
                  <Link to="/series/all?status=complete" className={`text-sm font-medium flex items-center ${rt.accentLink}`} data-id="element-12780" data-mp="39sbl3lthxl30">
                    See all <ChevronRightIcon className="h-4 w-4 ml-1" data-id="element-12781" data-mp="30vu80d1knd4m" />
                  </Link>
                </div>
                <SeriesCarousel series={completedSeries} darkMode={dk.isDark} data-id="element-12782" data-mp="2vwzspl2tc6p9" />
              </section>}

            {/* Ongoing Sagas */}
            {ongoingSeries.length > 0 && <section data-id="element-12783" data-mp="3oloiwp2byrm8">
                <div className="flex items-center justify-between mb-4" data-id="element-12784" data-mp="3jsc93l2b8pdj">
                  <div className="flex items-center gap-2" data-id="element-12785" data-mp="3jq9702nvv5sg">
                    <ClockIcon className="h-6 w-6 text-blue-500" data-id="element-12786" data-mp="3espw9cqe9b59" />
                    <div data-id="element-12787" data-mp="g40t5t63bcgx">
                      <h2 className={`text-xl font-bold ${dk.textHeading}`} data-id="element-12788" data-mp="g4ajxiyylnno">
                        Ongoing Sagas
                      </h2>
                      <p className={`text-sm ${dk.textSecondary}`} data-id="element-12789" data-mp="baxwhc60nmwi">
                        Follow along as new books release.
                      </p>
                    </div>
                  </div>
                  <Link to="/series/all?status=ongoing" className={`text-sm font-medium flex items-center ${rt.accentLink}`} data-id="element-12790" data-mp="z9yivn6dvz0z">
                    See all <ChevronRightIcon className="h-4 w-4 ml-1" data-id="element-12791" data-mp="pfy752gljvja" />
                  </Link>
                </div>
                <SeriesCarousel series={ongoingSeries} darkMode={dk.isDark} data-id="element-12792" data-mp="ghmjn9b11dv2" />
              </section>}

            {/* Epic Fantasy */}
            {fantasySeries.length > 0 && <section data-id="element-12793" data-mp="2k7rhwte2rze">
                <div className="flex items-center justify-between mb-4" data-id="element-12794" data-mp="6iwztazkljc6">
                  <h2 className={`text-xl font-bold ${dk.textHeading}`} data-id="element-12795" data-mp="6l9pkabkkxzs">
                    Epic Fantasy Series
                  </h2>
                  <Link to="/series/all?genre=fantasy" className={`text-sm font-medium flex items-center ${rt.accentLink}`} data-id="element-12796" data-mp="20xr854lgr9y3">
                    See all <ChevronRightIcon className="h-4 w-4 ml-1" data-id="element-12797" data-mp="3heggrihwcxsr" />
                  </Link>
                </div>
                <SeriesCarousel series={fantasySeries} darkMode={dk.isDark} data-id="element-12798" data-mp="3v5ldoctvrbze" />
              </section>}

            {/* Thrillers */}
            {thrillerSeries.length > 0 && <section data-id="element-12799" data-mp="8xbyf9dokon8">
                <div className="flex items-center justify-between mb-4" data-id="element-12800" data-mp="8wscziotni4g">
                  <h2 className={`text-xl font-bold ${dk.textHeading}`} data-id="element-12801" data-mp="3ztmda4d9tb0">
                    Edge-of-Your-Seat Thrillers
                  </h2>
                  <Link to="/series/all?genre=thriller" className={`text-sm font-medium flex items-center ${rt.accentLink}`} data-id="element-12802" data-mp="hy0avznfegy0">
                    See all <ChevronRightIcon className="h-4 w-4 ml-1" data-id="element-12803" data-mp="2jqessypyzlef" />
                  </Link>
                </div>
                <SeriesCarousel series={thrillerSeries} darkMode={dk.isDark} data-id="element-12804" data-mp="2et692tp6b9b2" />
              </section>}

            {/* Empty state */}
            {completedSeries.length === 0 && ongoingSeries.length === 0 && fantasySeries.length === 0 && thrillerSeries.length === 0 && <div className="text-center py-16" data-id="element-12805" data-mp="2v59e4uwltj7n">
                  <LibraryIcon className={`h-12 w-12 mx-auto mb-4 ${dk.textMuted}`} data-id="element-12806" data-mp="2v4zqhdz17o54" />
                  <h3 className={`text-lg font-medium mb-2 ${dk.textHeading}`} data-id="element-12807" data-mp="1xfwn6dj23339">
                    No series found
                  </h3>
                  <p className={`mb-4 ${dk.textSecondary}`} data-id="element-12808" data-mp="27fnfq9o2380p">
                    Try adjusting your filters.
                  </p>
                  <button onClick={resetFilters} className={`font-medium ${rt.accentLink}`} data-id="element-12809" data-mp="2c887l5nejb1l">
                    Clear filters
                  </button>
                </div>}

            {/* Bottom CTA */}
            <div className={`rounded-xl p-8 text-center border ${dk.border} ${rt.bgLight}`} data-id="element-12810" data-mp="1c5scbt2drpkn">
              <BookOpenIcon className={`h-12 w-12 mx-auto mb-4 ${rt.textPrimary}`} data-id="element-12811" data-mp="178a301kgb8c4" />
              <h2 className={`text-2xl font-bold mb-2 ${dk.textHeading}`} data-id="element-12812" data-mp="12aql8rojodff">
                Explore the Full Catalog
              </h2>
              <p className={`max-w-lg mx-auto mb-6 ${dk.textSecondary}`} data-id="element-12813" data-mp="t8hd28gr0w57">
                Browse thousands of series with advanced filters for genre,
                tropes, spice level, and completion status.
              </p>
              <Link to="/series/all" className={`inline-flex items-center px-6 py-3 rounded-lg font-bold text-white shadow-lg transition-transform hover:-translate-y-0.5 ${rt.bgDark} hover:opacity-90`} data-id="element-12814" data-mp="jj7hgimp4vcc">
                Browse All Series
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>;
};
export default Series;